import { FC, useState, useRef, ChangeEvent, DragEvent } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  IconButton,
  Grid,
} from '@chakra-ui/react';
import { photoService } from '@/services/firebase/photoService';
import { DogPhoto } from '@/types/dog';

interface PhotoUploaderProps {
  dogId: string;
  userId: string;
  onUploadComplete?: (photos: DogPhoto[]) => void;
}

interface FileWithPreview {
  file: File;
  preview: string;
  error?: string;
}

/**
 * 写真アップロードコンポーネント
 */
export const PhotoUploader: FC<PhotoUploaderProps> = ({
  dogId,
  userId,
  onUploadComplete,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/heic'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  /**
   * ファイルバリデーション
   */
  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      return '対応していないファイル形式です。JPEG、PNG、HEICのみアップロード可能です。';
    }

    if (file.size > MAX_FILE_SIZE) {
      return 'ファイルサイズが大きすぎます。5MB以下のファイルを選択してください。';
    }

    return null;
  };

  /**
   * ファイル選択処理
   */
  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newFiles: FileWithPreview[] = [];

    Array.from(files).forEach((file) => {
      const error = validateFile(file);
      const preview = URL.createObjectURL(file);

      newFiles.push({
        file,
        preview,
        error: error || undefined,
      });
    });

    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setUploadError(null);
  };

  /**
   * inputからのファイル選択
   */
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  /**
   * ドラッグオーバー
   */
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  /**
   * ドラッグリーブ
   */
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  /**
   * ドロップ
   */
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  /**
   * 個別ファイル削除
   */
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  /**
   * 全ファイルクリア
   */
  const handleClearAll = () => {
    selectedFiles.forEach((f) => URL.revokeObjectURL(f.preview));
    setSelectedFiles([]);
    setUploadError(null);
  };

  /**
   * アップロード実行
   */
  const handleUpload = async () => {
    // エラーのあるファイルを除外
    const validFiles = selectedFiles.filter((f) => !f.error);

    if (validFiles.length === 0) {
      setUploadError('アップロード可能なファイルがありません');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const uploadedPhotos = await photoService.uploadMultiplePhotos(
        validFiles.map((f) => f.file),
        dogId,
        userId
      );

      // プレビューURLを解放
      selectedFiles.forEach((f) => URL.revokeObjectURL(f.preview));
      setSelectedFiles([]);

      // コールバック実行
      if (onUploadComplete) {
        onUploadComplete(uploadedPhotos);
      }
    } catch (error) {
      console.error('アップロードエラー:', error);
      setUploadError(error instanceof Error ? error.message : 'アップロードに失敗しました');
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * ファイル選択ボタンクリック
   */
  const handleClickSelect = () => {
    fileInputRef.current?.click();
  };

  // 有効なファイル数
  const validFileCount = selectedFiles.filter((f) => !f.error).length;
  const hasError = selectedFiles.some((f) => f.error);

  return (
    <VStack gap={4} align="stretch">
      {/* ドラッグ&ドロップエリア */}
      <Box
        p={8}
        borderWidth={2}
        borderStyle="dashed"
        borderColor={isDragOver ? 'blue.500' : 'gray.300'}
        borderRadius="lg"
        bg={isDragOver ? 'blue.50' : 'gray.50'}
        textAlign="center"
        cursor="pointer"
        transition="all 0.2s"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClickSelect}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.heic"
          multiple
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />

        <VStack gap={2}>
          <Text fontSize="lg" fontWeight="medium" color="gray.700">
            📷 ここにファイルをドロップ
          </Text>
          <Text fontSize="sm" color="gray.600">
            または
          </Text>
          <Button colorPalette="blue" size="sm">
            ファイルを選択
          </Button>
          <Text fontSize="xs" color="gray.500" mt={2}>
            対応形式: JPEG, PNG, HEIC（最大5MB）
          </Text>
        </VStack>
      </Box>

      {/* 選択したファイルのプレビュー */}
      {selectedFiles.length > 0 && (
        <Box>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="medium">
              選択中: {validFileCount}枚
              {hasError && <Text as="span" color="red.500"> （エラーあり）</Text>}
            </Text>
            <Button size="sm" variant="ghost" onClick={handleClearAll}>
              全てクリア
            </Button>
          </HStack>

          <Grid templateColumns="repeat(auto-fill, minmax(120px, 1fr))" gap={3}>
            {selectedFiles.map((fileWithPreview, index) => (
              <Box key={index} position="relative">
                <Box
                  borderRadius="md"
                  overflow="hidden"
                  borderWidth={fileWithPreview.error ? 2 : 1}
                  borderColor={fileWithPreview.error ? 'red.500' : 'gray.200'}
                >
                  <Image
                    src={fileWithPreview.preview}
                    alt={fileWithPreview.file.name}
                    w="100%"
                    h="120px"
                    objectFit="cover"
                    opacity={fileWithPreview.error ? 0.5 : 1}
                  />
                </Box>

                {/* 削除ボタン */}
                <IconButton
                  aria-label="削除"
                  size="xs"
                  position="absolute"
                  top={1}
                  right={1}
                  colorPalette="red"
                  onClick={() => handleRemoveFile(index)}
                >
                  ✕
                </IconButton>

                {/* ファイル名 */}
                <Text
                  fontSize="xs"
                  mt={1}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  color={fileWithPreview.error ? 'red.500' : 'gray.600'}
                  title={fileWithPreview.file.name}
                >
                  {fileWithPreview.file.name}
                </Text>

                {/* エラーメッセージ */}
                {fileWithPreview.error && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    {fileWithPreview.error}
                  </Text>
                )}
              </Box>
            ))}
          </Grid>
        </Box>
      )}

      {/* エラー表示 */}
      {uploadError && (
        <Box p={3} bg="red.50" borderRadius="md" borderWidth={1} borderColor="red.200">
          <Text fontSize="sm" color="red.700">
            {uploadError}
          </Text>
        </Box>
      )}

      {/* アップロードボタン */}
      {selectedFiles.length > 0 && (
        <Button
          colorPalette="blue"
          w="100%"
          onClick={handleUpload}
          disabled={isUploading || validFileCount === 0}
          loading={isUploading}
        >
          {isUploading ? 'アップロード中...' : `${validFileCount}枚の写真をアップロード`}
        </Button>
      )}
    </VStack>
  );
};
